// Settings surface primitives — dense-preference-row style (Slack / iOS System).
// The Settings page composes Section + PrefRow + NavRow + ExpandableRow to
// render its entire surface. Existing dialogs stay untouched; rows just
// trigger the same open-state setters.
//
// Rows always take: an icon, a colored icon-background, a title, a description,
// and either a right-hand `action` node OR a chevron (for nav rows).

import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight, ChevronDown } from "lucide-react";

// ─── Colored icon square ────────────────────────────────────────────────────

const ICON_TINTS = {
  indigo:  { bg: "bg-indigo-50 dark:bg-indigo-900/25",  fg: "text-indigo-600 dark:text-indigo-400"  },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/25", fg: "text-emerald-600 dark:text-emerald-400"},
  amber:   { bg: "bg-amber-50 dark:bg-amber-900/25",    fg: "text-amber-600 dark:text-amber-400"    },
  rose:    { bg: "bg-rose-50 dark:bg-rose-900/25",      fg: "text-rose-600 dark:text-rose-400"      },
  violet:  { bg: "bg-violet-50 dark:bg-violet-900/25",  fg: "text-violet-600 dark:text-violet-400"  },
  teal:    { bg: "bg-teal-50 dark:bg-teal-900/25",      fg: "text-teal-600 dark:text-teal-400"      },
  sky:     { bg: "bg-sky-50 dark:bg-sky-900/25",        fg: "text-sky-600 dark:text-sky-400"        },
  slate:   { bg: "bg-slate-100 dark:bg-slate-800",      fg: "text-slate-600 dark:text-slate-300"    },
};

function IconTile({ icon: Icon, color = "indigo", size = 10 }) {
  const t = ICON_TINTS[color] || ICON_TINTS.indigo;
  const sizeCls = size === 8 ? "w-8 h-8" : size === 9 ? "w-9 h-9" : "w-10 h-10";
  const iconCls = size === 8 ? "w-4 h-4" : "w-[18px] h-[18px]";
  return (
    <div className={`${sizeCls} rounded-xl ${t.bg} ${t.fg} flex items-center justify-center shrink-0`}>
      <Icon className={iconCls} strokeWidth={2.1} />
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────────────

export function Section({ title, description, children, testId }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <div className="px-1">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{title}</h2>
        {description && (
          <p className="text-[13px] text-slate-500 dark:text-slate-500 mt-0.5">{description}</p>
        )}
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden shadow-[0_1px_3px_rgb(0,0,0,0.02)]">
        {children}
      </div>
    </section>
  );
}

// ─── PrefRow: static row with a right-side action node ─────────────────────

export function PrefRow({ icon, iconColor, title, description, action, badge, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid={testId}>
      {icon && <IconTile icon={icon} color={iconColor} />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-[14.5px] text-slate-900 dark:text-white truncate">{title}</div>
          {badge != null && badge !== "" && (
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
              {badge}
            </span>
          )}
        </div>
        {description && <div className="text-[12.5px] text-slate-500 dark:text-slate-400 truncate">{description}</div>}
      </div>
      {action}
    </div>
  );
}

// ─── NavRow: whole row is clickable, ends with a chevron ───────────────────

export function NavRow({ icon, iconColor, title, description, to, onClick, external, testId }) {
  const inner = (
    <>
      {icon && <IconTile icon={icon} color={iconColor} />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[14.5px] text-slate-900 dark:text-white truncate">{title}</div>
        {description && <div className="text-[12.5px] text-slate-500 dark:text-slate-400 truncate">{description}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </>
  );
  const cls = "flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer w-full text-left";
  if (onClick && !to) {
    return <button className={cls} onClick={onClick} data-testid={testId}>{inner}</button>;
  }
  if (external) {
    return <a href={to} className={cls} data-testid={testId}>{inner}</a>;
  }
  return <NavLink to={to} className={cls} data-testid={testId}>{inner}</NavLink>;
}

// ─── ExpandableRow: click to unfold rich content inline ────────────────────

export function ExpandableRow({
  icon, iconColor, title, description, badge, testId,
  defaultOpen = false, children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors w-full text-left"
        aria-expanded={open}
      >
        {icon && <IconTile icon={icon} color={iconColor} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[14.5px] text-slate-900 dark:text-white truncate">{title}</div>
            {badge != null && badge !== "" && (
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                {badge}
              </span>
            )}
          </div>
          {description && <div className="text-[12.5px] text-slate-500 dark:text-slate-400 truncate">{description}</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-slate-50/50 dark:bg-slate-800/20 border-t border-slate-100 dark:border-slate-800">
          {children}
        </div>
      )}
    </div>
  );
}
